import { BTLocalStorage } from "types/btStorage";
import { DirectoryType, DocumentInstanceType, MediaType } from "types/enum";
import { documentInstanceTypeMultiplier, rootLevelFolderId } from "types/MediaConstants";

import { APIHandler, type IBlobResponse } from "utilities/apiHandler";

import { JobIdTypes } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { type SelectedFilterItem } from "entity/filters/Filter/Filter.api.types";
import { getFilterStringFromItems } from "entity/filters/filters.utils";
import {
    DeletedFoldersResponse,
    type IDownloadZipOfAttachedFilesRequest,
    type IDownloadZipOfFolderRequest,
    type IFileFolderListRequest,
    type IMediaDeleteRequest,
    type IMediaListSort,
    MediaListEntity,
    type SelectedMediaListViews,
} from "entity/media/common/mediaTypes";
import {
    type IGetLinkedDocumentInstancesRequest,
    MediaReplaceEntity,
} from "entity/media/MediaReplace/MediaReplace.api.types";

export interface IMediaListHandler {
    getMainDirectory(mediaType: MediaType, jobId: number): Promise<MediaListEntity>;
    getFolder(
        directoryType: DirectoryType,
        mediaType: MediaType,
        folderId: number,
        jobId: number,
        filters?: SelectedFilterItem[],
    ): Promise<MediaListEntity>;
    getLinkedDocumentInstances(
        documentInstanceId: number,
        includeThisDoc?: boolean,
        linkedByAnnotationGroup?: boolean,
    ): Promise<MediaReplaceEntity>;
    getSavedListView(mediaType: MediaType): SelectedMediaListViews;
    setSavedListView(selectedListView: SelectedMediaListViews, mediaType: MediaType): void;
    getSavedSort(mediaType: MediaType, directoryType: DirectoryType): IMediaListSort | undefined;
    setSavedSort(sort: IMediaListSort, mediaType: MediaType, directoryType: DirectoryType): void;
    deleteMedia(
        folderIds: number[],
        fileIds: number[],
        jobId: number,
        mediaType: number,
    ): Promise<DeletedFoldersResponse>;
    downloadZipOfFolder(data: IDownloadZipOfFolderRequest): Promise<IBlobResponse>;
    downloadZipOfAttachedDocs(data: IDownloadZipOfAttachedFilesRequest): Promise<IBlobResponse>;
}

/**
 * Maps a media type to its sort-persistence storage key. Returns undefined for unrecognised media
 * types so callers no-op gracefully instead of writing an unknown key.
 */
function getMediaSortStorageKey(
    mediaType: MediaType,
):
    | "bt-object-selectedPhotoSort"
    | "bt-object-selectedDocumentSort"
    | "bt-object-selectedVideoSort"
    | undefined {
    switch (mediaType) {
        case MediaType.Photo:
            return "bt-object-selectedPhotoSort";
        case MediaType.Document:
            return "bt-object-selectedDocumentSort";
        case MediaType.Video:
            return "bt-object-selectedVideoSort";
        default:
            return undefined;
    }
}

/**
 * Serializes a GridSort (class instance) to a plain object safe for localStorage.
 * GridSort is a class; localStorage cannot persist class instances.
 */
export const toPlainGridSort = (
    gridSort: IMediaListSort["fileSort"],
): { sortColumn: string; sortDirection: "asc" | "desc" | undefined } | undefined =>
    gridSort
        ? { sortColumn: gridSort.sortColumn, sortDirection: gridSort.sortDirection }
        : undefined;

export class MediaListHandler implements IMediaListHandler {
    async getMainDirectory(mediaType: MediaType, jobId: number): Promise<MediaListEntity> {
        const requestData: IFileFolderListRequest = {
            mediaType,
            folderId: rootLevelFolderId,
            associatedTypeId: DocumentInstanceType.StandaloneDocument,
            directoryType:
                jobId === JobIdTypes.GlobalDocs ? DirectoryType.GlobalDocs : DirectoryType.Standard,
            jobId,
        };
        return await APIHandler("/api/MediaFolders/MainDirectory", {
            method: "GET",
            data: requestData,
            responseType: MediaListEntity,
        });
    }

    async getFolder(
        directoryType: DirectoryType,
        mediaType: MediaType,
        folderId: number,
        jobId: number,
        filters?: SelectedFilterItem[],
    ): Promise<MediaListEntity> {
        const requestData: IFileFolderListRequest = {
            mediaType,
            folderId,
            associatedTypeId:
                directoryType === DirectoryType.AttachedFiles &&
                folderId % documentInstanceTypeMultiplier === 0
                    ? folderId / documentInstanceTypeMultiplier
                    : DocumentInstanceType.StandaloneDocument,
            directoryType,
            jobId,
        };
        return await APIHandler("/api/MediaFolders/GetDirectoryDetails", {
            method: "GET",
            data: { ...requestData, filters: getFilterStringFromItems(filters) },
            responseType: MediaListEntity,
        });
    }

    async getLinkedDocumentInstances(
        documentInstanceId: number,
        includeThisDoc?: boolean,
        linkedByAnnotationGroup?: boolean,
    ): Promise<MediaReplaceEntity> {
        const request: IGetLinkedDocumentInstancesRequest = {
            documentInstanceId,
            includeThisDoc,
            linkedByAnnotationGroup,
        };

        return await APIHandler("/api/Documents/GetLinkedDocumentInstances", {
            method: "GET",
            data: request,
            responseType: MediaReplaceEntity,
        });
    }

    getSavedListView(mediaType: MediaType): SelectedMediaListViews {
        switch (mediaType) {
            case MediaType.Document:
                return BTLocalStorage.get("bt-string-selectedDocumentListView");
            case MediaType.Photo:
                return BTLocalStorage.get("bt-string-selectedPhotoListView");
            case MediaType.Video:
                return BTLocalStorage.get("bt-string-selectedVideoListView");
            default:
                return "Tile";
        }
    }

    setSavedListView(selectedListView: SelectedMediaListViews, mediaType: MediaType): void {
        switch (mediaType) {
            case MediaType.Document:
                BTLocalStorage.set("bt-string-selectedDocumentListView", selectedListView);
                break;
            case MediaType.Photo:
                BTLocalStorage.set("bt-string-selectedPhotoListView", selectedListView);
                break;
            case MediaType.Video:
                BTLocalStorage.set("bt-string-selectedVideoListView", selectedListView);
                break;
            default:
                break;
        }
    }

    getSavedSort(mediaType: MediaType, directoryType: DirectoryType): IMediaListSort | undefined {
        const key = getMediaSortStorageKey(mediaType);
        if (!key) {
            return undefined;
        }
        return BTLocalStorage.get(key)[directoryType];
    }

    setSavedSort(sort: IMediaListSort, mediaType: MediaType, directoryType: DirectoryType): void {
        const key = getMediaSortStorageKey(mediaType);
        if (!key) {
            return;
        }

        BTLocalStorage.set(key, {
            ...BTLocalStorage.get(key),
            [directoryType]: {
                fileSort: toPlainGridSort(sort.fileSort),
                folderSort: toPlainGridSort(sort.folderSort),
            },
        });
    }

    async deleteMedia(
        folderIds: number[],
        fileIds: number[],
        jobId: number,
        mediaType: MediaType,
    ): Promise<DeletedFoldersResponse> {
        const data: IMediaDeleteRequest = {
            folderIds,
            fileIds,
            jobId,
            mediaType,
        };
        return await APIHandler("/api/Media", {
            method: "DELETE",
            data,
            responseType: DeletedFoldersResponse,
        });
    }

    async downloadZipOfFolder(data: IDownloadZipOfFolderRequest): Promise<IBlobResponse> {
        return await APIHandler("/api/MediaFolders/DownloadZipOfFolder", {
            method: "POST",
            data,
            responseType: "blob",
        });
    }

    async downloadZipOfAttachedDocs(
        data: IDownloadZipOfAttachedFilesRequest,
    ): Promise<IBlobResponse> {
        return await APIHandler("/api/Media/DownloadZipOfAttachedDocs", {
            method: "POST",
            data,
            responseType: "blob",
        });
    }
}
